import { jsx } from 'react/jsx-runtime';
import { forwardRef, useLayoutEffect } from 'react';
import { isIncrementFormatter } from '../formatter/formatter.utils.js';
import { useMergeRefs } from '../hooks/use-merge-refs.js';
import { Input } from '../input/input.js';
import { useFormattedInput } from './hooks/use-formatted-input.js';
import { FormattedInputContext, useSetFormattedInputContext, useFormattedInputContext } from './formatted-input.context.js';
import { Stepper as Stepper$1 } from './stepper.js';

/**
 * An input that is configured with a formatter object. The input converts from the type the
 * formatter is configured to accept (e.g. a number) to a string for display, and back again
 * on change. If the FormattedInput is passed an `IncrementFormatter`, it will also handle up
 * and down arrow keys to increment and decrement the value.
 *
 * By default, FormattedInput is semi-controlled, meaning it does not call onChange as the user
 * types, but instead waits for the user to blur the input or hit enter. If a new value is passed
 * via props while the user is mid-edit, the visible value won't update.
 *
 * For up/down arrow keys, onChange is called immediately for every keystroke.
 *
 * @compose { "root": true, "strictChildren": true }
 * @see https://fpl.figma.design/components/inputs/formatted-input/
 */
const Root = forwardRef((props, ref) => (jsx(FormattedInputContext, { children: jsx(Input.Root, { "data-fpl-component": "", ...props, ref: ref }) })));
Root.displayName = 'FormattedInput.Root';
const _Field = forwardRef((props, refIn) => {
    const setCtx = useSetFormattedInputContext();
    const ctx = useFormattedInput(props);
    // The `setCtx` function comes from useState, which is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useLayoutEffect(() => setCtx(ctx), [ctx]);
    const { inputProps } = ctx;
    const ref = useMergeRefs(refIn, inputProps.ref);
    return jsx(Input, { "data-fpl-component": "", ...inputProps, ref: ref });
});
_Field.displayName = 'FormattedInput.Field';
/**
 * The typeable portion of the formatted input. Must be a direct child of `FormattedInput.Root`.
 *
 * @compose { "parent": ["FormattedInput.Root"] }
 */
const Field = _Field;
/**
 * Place as the last child of `FormattedInput.Root` to provide up/down arrow buttons.
 *
 * The stepper buttons do not render if the input is disabled, or if the formatter passed to the
 * input is not an `IncrementFormatter`.
 *
 * @note requires this input is set up with an IncrementFormatter, otherwise doesn't render.
 * @compose { "parent": ["FormattedInput.Root"] }
 */
const Stepper = forwardRef((_, ref) => {
    const ctx = useFormattedInputContext();
    if (!ctx || ctx.inputProps.disabled || !isIncrementFormatter(ctx.formatter)) {
        return null;
    }
    return (jsx(Stepper$1, { "data-fpl-component": "", ref: ref, value: ctx.value, formatter: ctx.formatter, getStringValue: ctx.getStringValue, onChange: (value) => ctx.onChange(value, { commit: true, source: 'stepper' }), onChangeRestricted: ctx.onChangeRestricted }));
});
Stepper.displayName = 'FormattedInput.Stepper';

export { Field, Root, Stepper };
//# sourceMappingURL=formatted-input.js.map
